import React, { useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const RegisterPage = ({ setCurrentPage }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    birthDate: '',
    password: '',
    acceptTerms: false,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');

    // Basit validasyon
    if (!formData.acceptTerms) {
      setErrMsg('Kullanıcı sözleşmesini onaylamanız gerekiyor.');
      return;
    }
    if (formData.password.length < 8) {
      setErrMsg('Şifre en az 8 karakter olmalıdır.');
      return;
    }
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setErrMsg('Ad ve soyad alanları zorunludur.');
      return;
    }

    try {
      setLoading(true);

      // Auth: kullanıcı oluştur
      const cred = await createUserWithEmailAndPassword(auth, formData.email.trim(), formData.password);
      const user = cred.user;

      // Profil adını güncelle
      const displayName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
      await updateProfile(user, { displayName });

      // Firestore: users koleksiyonuna kaydet
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        displayName,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        birthDate: formData.birthDate || null, // "YYYY-MM-DD" string
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // future: roles, preferences vs.
      });

      // Başarılı → login sayfasına yönlendirebilirsin
      setCurrentPage?.('login');
    } catch (err) {
      // Hata mesajını kullanıcı dostu göster
      let msg = 'Kayıt sırasında bir hata oluştu.';
      if (err.code === 'auth/email-already-in-use') msg = 'Bu e-posta ile daha önce kayıt olunmuş.';
      if (err.code === 'auth/invalid-email') msg = 'Geçerli bir e-posta adresi giriniz.';
      if (err.code === 'auth/weak-password') msg = 'Şifre çok zayıf. En az 8 karakter giriniz.';
      setErrMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-gray-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Logo ve Başlık */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-400 rounded-full mb-4">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">Üyelik Oluştur</h2>
          <p className="text-gray-600 mt-2">Hesabını oluştur, sonra firma bilgilerini ekleyebilirsin.</p>
        </div>

        {/* Form Kartı */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Ad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ad *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="Adınız"
                  required
                />
              </div>
              {/* Soyad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Soyad *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="Soyadınız"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">E-posta *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="ornek@email.com"
                required
              />
            </div>

            {/* Doğum Tarihi */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Doğum Tarihi</label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>

            {/* Şifre */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Şifre *</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="En az 8 karakter"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm"
                  aria-label={showPwd ? 'Şifreyi gizle' : 'Şifreyi göster'}
                >
                  {showPwd ? 'Gizle' : 'Göster'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                En az 8 karakter, tercihen büyük/küçük harf ve rakam içersin.
              </p>
            </div>

            {/* Sözleşme */}
            <div className="mt-5">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-amber-500 focus:ring-amber-400 border-gray-300 rounded"
                  required
                />
                <span className="text-sm text-gray-600">
                  <button type="button" className="text-amber-600 hover:underline">Kullanıcı Sözleşmesi</button> ve{' '}
                  <button type="button" className="text-amber-600 hover:underline">Gizlilik Politikası</button>’nı okudum ve onaylıyorum *
                </span>
              </label>
            </div>

            {/* Hata */}
            {errMsg && (
              <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {errMsg}
              </div>
            )}

            {/* Aksiyonlar */}
            <div className="mt-6 flex justify-between items-center">
              <button
                type="button"
                onClick={() => setCurrentPage?.('login')}
                className="text-sm text-gray-600 hover:text-amber-600 font-medium"
              >
                Zaten hesabın var mı? <span className="underline">Giriş yap</span>
              </button>

              <button
                type="submit"
                disabled={loading}
                className="bg-amber-400 hover:bg-amber-500 disabled:opacity-60 text-white font-semibold px-6 py-2 rounded-lg transition"
              >
                {loading ? 'Kaydediliyor…' : 'Üye Ol'}
                <svg className="inline-block w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            </div>
          </form>
        </div>

        {/* Alt bilgi */}
        <div className="text-center mt-6 text-xs text-gray-500">
          Üye olduktan sonra “Profilim &gt; Firmalarım” alanından firma ekleyebilirsiniz.
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
